import config from "../../config";
import localDay from "./lib/localDay";

// Same auth pattern as the other controllers (note the double space — matches
// api.service.js authHeader).
function authHeader() {
  const user = JSON.parse(localStorage.getItem("user"));
  if (user && user.key) {
    return { Authorization: "Token  " + user.key };
  }
  return {};
}

async function req(path, { method = "GET", body } = {}) {
  const headers = Object.assign({ "Content-Type": "application/json" }, authHeader());
  const request = new Request(`${config.apiGateway.URL}${path}`, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  });
  const response = await fetch(request);
  let json = null;
  try {
    json = await response.json();
  } catch (e) {
    json = null;
  }
  return { json, status: response.status };
}

// Multipart variant for file uploads — must NOT set Content-Type so the browser
// adds the multipart boundary itself.
async function reqMultipart(path, formData, { method = "POST" } = {}) {
  const request = new Request(`${config.apiGateway.URL}${path}`, {
    method,
    headers: authHeader(),
    body: formData,
  });
  const response = await fetch(request);
  let json = null;
  try {
    json = await response.json();
  } catch (e) {
    json = null;
  }
  return { json, status: response.status };
}

// `tasks`: "open" | "overdue" — practices with outstanding work on any office.
// `status`: "active" (server default) | "dormant" | "all" — dormant is per office
// and a practice reads dormant only when every office is.
// `date`: the browser's YYYY-MM-DD (lib/localDay) — the server buckets "overdue"
// against the caller's day, not its own UTC clock.
export const getPractices = ({ page = 1, pageSize = 25, search, stage, active, tasks, status, date } = {}) => {
  let q = `?page=${page}&page_size=${pageSize}`;
  if (search) q += `&search=${encodeURIComponent(search)}`;
  if (stage) q += `&stage=${encodeURIComponent(stage)}`;
  if (active !== undefined && active !== "") q += `&active=${active}`;
  if (tasks) q += `&tasks=${encodeURIComponent(tasks)}`;
  if (status) q += `&status=${encodeURIComponent(status)}`;
  if (date) q += `&date=${encodeURIComponent(date)}`;
  return req(`/crm/practices/${q}`);
};

// --- Today: the super admin's plate -----------------------------------------
// One call, one list. `scope`: "me" (mine + unassigned, the default) | "all" |
// a user id; `include`: module names to build (tasks, invites, threads, leads,
// requests, ftc) — omit for all. Row actions reuse the endpoints below
// (updateOfficeNote, resendInvite, revokeInvite, reviewPlatformRequest).
export const getToday = ({ date, scope, include } = {}) => {
  const q = new URLSearchParams();
  if (date) q.set("date", date);
  if (scope) q.set("scope", scope);
  if (include && include.length) q.set("include", include.join(","));
  const s = q.toString();
  return req(`/crm/today/${s ? "?" + s : ""}`);
};

// "Not on my plate": hides a non-task Today item until it changes (the thread's
// next inbound message, an invite resend, a new lead submission). Team-wide.
export const dismissTodayItem = (kind, id, version) =>
  req(`/crm/today/dismiss/`, { method: "POST", body: { kind, id: String(id), version: version || "" } });
export const restoreTodayItem = (kind, id) =>
  req(`/crm/today/restore/`, { method: "POST", body: { kind, id: String(id) } });

// Accept or reject a suggestion raised in the app (the CRM Requests queue).
export const reviewPlatformRequest = (id, action, note = "") =>
  req(`/crm/requests/${id}/${encodeURIComponent(action)}/`, { method: "POST", body: { note } });

export const getPracticeDetail = (id) => req(`/crm/practices/${id}/`);

// Soft-delete: hides the clinic from the CRM list (kept in the DB).
export const deletePractice = (id) => req(`/crm/practices/${id}/`, { method: "DELETE" });

// Pin/unpin a practice to the top of the Practices table (team-wide).
export const pinPractice = (id) => req(`/crm/practices/${id}/pin/`, { method: "POST" });
export const unpinPractice = (id) => req(`/crm/practices/${id}/pin/`, { method: "DELETE" });

export const getInvites = ({ page = 1, pageSize = 25, search, state, type } = {}) => {
  let q = `?page=${page}&page_size=${pageSize}`;
  if (search) q += `&search=${encodeURIComponent(search)}`;
  if (state) q += `&state=${encodeURIComponent(state)}`;
  if (type) q += `&type=${encodeURIComponent(type)}`;
  return req(`/crm/invites/${q}`);
};

export const createInvite = (email, trialEnabled = false, extra = {}) =>
  req(`/crm/invites/`, {
    method: "POST",
    body: {
      receiver_email: email,
      trial_enabled: !!trialEnabled,
      practice_name: extra.practiceName || "",
      contact_person_name: extra.contactPersonName || "",
      contact_phone: extra.contactPhone || "",
      // false => create the invite but send no email, for an admin registering
      // on the customer's behalf. The response still carries `invite_link`.
      send_email: extra.sendEmail === undefined ? true : !!extra.sendEmail,
      // Sent from a lead: the invite remembers which, the lead reads "invited",
      // and the registrant is linked back to it at signup.
      ...(extra.leadId ? { lead_id: extra.leadId } : {}),
    },
  });

export const resendInvite = (id) => req(`/crm/invites/${id}/resend/`, { method: "POST" });

export const revokeInvite = (id) => req(`/crm/invites/${id}/revoke/`, { method: "POST" });

export const verifyUserEmail = (id) => req(`/crm/users/${id}/verify-email/`, { method: "POST" });

export const sendFollowUp = ({ practiceId, email, templateKey = "crm_onboarding_followup" }) =>
  req(`/crm/follow-up/`, {
    method: "POST",
    body: { practice_id: practiceId, email, template_key: templateKey },
  });

// --- Office-centric CRM ----------------------------------------------------
export const getOfficeDetail = (officeId) => req(`/crm/offices/${officeId}/`);

export const completeOfficeSetup = (practiceId, officeId, body) =>
  req(`/crm/practices/${practiceId}/offices/${officeId}/complete-setup/`, { method: "POST", body });

// Public plans; with officeId also the special plans offered to that office,
// each priced for it (`charge`). Rows carry family, term_months and FTC limits.
export const getSubscriptionTiers = (officeId) =>
  req(`/crm/subscription-tiers/${officeId ? `?office_id=${officeId}` : ""}`);

// Users & Billing (office and practice drawers). Super admins only.
export const getOfficeBilling = (officeId) => req(`/crm/offices/${officeId}/billing/`);
export const getPracticeBilling = (practiceId) => req(`/crm/practices/${practiceId}/billing/`);
// {office_id | practice_id, kind: "discount"|"plan", tier_ids, discount_type, discount_value, label, expires_on}
export const createOffer = (body) => req(`/crm/offers/`, { method: "POST", body });
// {label?, expires_on?, revoke?: true}
export const updateOffer = (offerId, patch) => req(`/crm/offers/${offerId}/`, { method: "PATCH", body: patch });
// Special plans — the templates (every one ever made, expired offers or not).
export const getCustomPlans = () => req(`/crm/custom-plans/`);
// {base_tier_id, name, variants: {annual?: {price}, monthly?: {price}}, descriptions, limit_ftc_daily, limit_ftc_monthly}
export const createCustomPlan = (body) => req(`/crm/custom-plans/`, { method: "POST", body });
export const updateCustomPlan = (tierId, patch) =>
  req(`/crm/custom-plans/${tierId}/`, { method: "PATCH", body: patch });
// Global switches: {free_trials_enabled, require_plan}; GET also lists `would_block`.
export const getPlanSettings = () => req(`/crm/plan-settings/`);
export const updatePlanSettings = (patch) => req(`/crm/plan-settings/`, { method: "PATCH", body: patch });
// Sales tax. Lines are [{label, percent}] ("9.975" for 9.975%); the server
// validates and answers 400 {detail} with the reason. `code` is "DEFAULT" or a
// province ("AB"); a region write answers the whole overview again.
export const getSalesTax = () => req(`/crm/sales-tax/`);
export const saveTaxRegion = (code, lines) => req(`/crm/sales-tax/${code}/`, { method: "PUT", body: { lines } });
export const clearTaxRegion = (code) => req(`/crm/sales-tax/${code}/`, { method: "DELETE" });
// {province?: "AB", rule?: {lines, reason} | null} — answers the office's tax card.
export const updateOfficeSalesTax = (officeId, patch) =>
  req(`/crm/offices/${officeId}/sales-tax/`, { method: "PATCH", body: patch });
// {rule: {lines, reason} | null} — answers the practice's tax card.
export const updatePracticeSalesTax = (practiceId, patch) =>
  req(`/crm/practices/${practiceId}/sales-tax/`, { method: "PATCH", body: patch });

// Activity analytics. `period` is daily|weekly|monthly; `buckets` is how many
// trailing buckets to return (the API defaults and caps it).
export const getOfficeActivity = (officeId, { period, buckets } = {}) => {
  const qs = new URLSearchParams();
  if (period) qs.set("period", period);
  if (buckets) qs.set("buckets", buckets);
  const q = qs.toString();
  return req(`/crm/offices/${officeId}/activity/${q ? `?${q}` : ""}`);
};

// Notes
export const getOfficeNotes = (officeId) => req(`/crm/offices/${officeId}/notes/`);
// A note can also be a task: `task` carries {is_task, due_date, assignee_id}.
export const updateOfficeNote = (officeId, noteId, patch) =>
  req(`/crm/offices/${officeId}/notes/${noteId}/`, { method: "PATCH", body: patch });
export const deleteOfficeNote = (officeId, noteId) =>
  req(`/crm/offices/${officeId}/notes/${noteId}/`, { method: "DELETE" });
// Who a task can be assigned to — super admins + FTC personnel.
export const getTaskAssignees = () => req(`/crm/task-assignees/`);
export const addOfficeNote = (officeId, bodyText, task = null) =>
  req(`/crm/offices/${officeId}/notes/`, {
    method: "POST",
    body: {
      body: bodyText,
      ...(task && task.is_task
        ? { is_task: true, due_date: task.due_date || null, assignee_id: task.assignee_id || null }
        : {}),
    },
  });

// Tracked email addresses
export const getTrackedEmails = (officeId) => req(`/crm/offices/${officeId}/tracked-emails/`);
export const addTrackedEmail = (officeId, email, label = "") =>
  req(`/crm/offices/${officeId}/tracked-emails/`, { method: "POST", body: { email, label } });
export const updateTrackedEmail = (id, patch) =>
  req(`/crm/tracked-emails/${id}/`, { method: "PATCH", body: patch });
export const deleteTrackedEmail = (id) =>
  req(`/crm/tracked-emails/${id}/`, { method: "DELETE" });

// Free-trial cap. Usage is counted live, so setting the quota IS the extend/end
// control: above what they've used revives the trial, at or below ends it.
// `quota: null` reverts the office to the global default.
export const getOfficeTrial = (officeId) =>
  req(`/crm/offices/${officeId}/trial/`);
export const setOfficeTrialQuota = (officeId, quota) =>
  req(`/crm/offices/${officeId}/trial/`, { method: "PATCH", body: { quota } });

// Contact + reminder suppression
export const updateOfficeContact = (officeId, patch) =>
  req(`/crm/offices/${officeId}/contact/`, { method: "PATCH", body: patch });
export const updateOfficeReminders = (officeId, patch) =>
  req(`/crm/offices/${officeId}/reminders/`, { method: "PATCH", body: patch });

// Per-office feature switches (office drawer → Features tab).
export const getOfficeFeatures = (officeId) => req(`/crm/offices/${officeId}/features/`);

// --- Breakdown format: the office's own breakdown sheet ---------------------
// Upload → a draft read in the background → review/correct → activate. Only the
// active version changes the office's exports (crm/views_breakdown_formats.py).
export const getBreakdownFormats = (officeId) => req(`/crm/offices/${officeId}/breakdown-formats/`);
export const uploadBreakdownFormat = (officeId, file, instructions) => {
  const fd = new FormData();
  fd.append("file", file);
  if (instructions) fd.append("instructions", instructions);
  return reqMultipart(`/crm/offices/${officeId}/breakdown-formats/`, fd);
};
export const getBreakdownFormat = (id) => req(`/crm/breakdown-formats/${id}/`);
export const updateBreakdownFormat = (id, body) => req(`/crm/breakdown-formats/${id}/`, { method: "PATCH", body });
export const deleteBreakdownFormat = (id) => req(`/crm/breakdown-formats/${id}/`, { method: "DELETE" });
export const activateBreakdownFormat = (id) => req(`/crm/breakdown-formats/${id}/activate/`, { method: "POST" });
export const deactivateBreakdownFormat = (id) => req(`/crm/breakdown-formats/${id}/deactivate/`, { method: "POST" });
export const regenerateBreakdownFormat = (id, instructions) =>
  req(`/crm/breakdown-formats/${id}/regenerate/`, { method: "POST", body: { instructions: instructions || "" } });
// The upload as sent, as a Blob (PHI-safe: served only through the logged-in view).
export const getBreakdownFormatSource = async (id) => {
  const response = await fetch(new Request(`${config.apiGateway.URL}/crm/breakdown-formats/${id}/source/`, {
    method: "GET", headers: authHeader(),
  }));
  return { status: response.status, blob: response.ok ? await response.blob() : null };
};
export const updateOfficeFeatures = (officeId, patch) =>
  req(`/crm/offices/${officeId}/features/`, { method: "PATCH", body: patch });

// Office drawer → Features → "Requests our team starts": do requests our FTC team
// starts itself count toward this office's usage? { count_ftc_requests: bool }.
export const getOfficeUsageSettings = (officeId) => req(`/crm/offices/${officeId}/usage-settings/`);
export const updateOfficeUsageSettings = (officeId, patch) =>
  req(`/crm/offices/${officeId}/usage-settings/`, { method: "PATCH", body: patch });

// Contact people (multiple per office; shown as the hero carousel cards)
export const getOfficeContacts = (officeId) => req(`/crm/offices/${officeId}/contacts/`);
// email is NOT optional here in the way it looks: the address is what the mail
// ingest matches on and what FTC access is granted over, so a contact created
// without one is invisible to the whole correspondence feature. It was missing
// from this destructure since 10a4bc3f — the form collected it and this dropped
// it on the floor, silently, while editing the same contact afterwards worked.
export const addOfficeContact = (officeId, { name, title, phone, email, receives_2fa_alerts }) =>
  req(`/crm/offices/${officeId}/contacts/`, {
    method: "POST",
    body: { name, title, phone, email, receives_2fa_alerts },
  });
export const updateOfficeContactCard = (id, patch) =>
  req(`/crm/office-contacts/${id}/`, { method: "PATCH", body: patch });
export const deleteOfficeContact = (id) =>
  req(`/crm/office-contacts/${id}/`, { method: "DELETE" });

// --- The email list (office and lead drawers) ------------------------------
// Every email, newest first, 50 a page. `mailbox`: "mailbox:<id>" (one of our
// inboxes) or "ftc:<user id>"; `q`: subject / sender / preview / any address.
function emailQuery({ page, mailbox, q } = {}) {
  const p = new URLSearchParams();
  if (page && page > 1) p.set("page", String(page));
  if (mailbox) p.set("mailbox", mailbox);
  if (q) p.set("q", q);
  const s = p.toString();
  return s ? `?${s}` : "";
}
export const getOfficeEmails = (officeId, opts) => req(`/crm/offices/${officeId}/emails/${emailQuery(opts)}`);
export const getLeadEmails = (leadId, opts) => req(`/crm/leads/${leadId}/emails/${emailQuery(opts)}`);
/** One email with its body and attachments. */
export const getEmail = (id) => req(`/crm/emails/${id}/`);
/** {body_html, reply_all?, attachment_ids?, mailbox_id?} — answers THAT email. */
export const replyToEmail = (id, body) => req(`/crm/emails/${id}/reply/`, { method: "POST", body });
/** {to?, cc?, subject, body_html, attachment_ids?, mailbox_id?} — filed under the lead. */
export const composeLeadEmail = (leadId, body) => req(`/crm/leads/${leadId}/compose/`, { method: "POST", body });

// --- Leads made from email -------------------------------------------------
/** Delete a lead the CRM made from email and ignore its address ("address") or company domain ("domain"). */
export const notALead = (leadId, scope) => req(`/crm/leads/${leadId}/not-a-lead/`, { method: "POST", body: { scope } });
export const getIgnoredSenders = () => req(`/crm/ignored-senders/`);
/** An address ("jane@vendor.com") or a domain ("vendor.com"). */
export const addIgnoredSender = (value, note = "") => req(`/crm/ignored-senders/`, { method: "POST", body: { value, note } });
export const deleteIgnoredSender = (id) => req(`/crm/ignored-senders/${id}/`, { method: "DELETE" });

// --- Email conversations (the old thread view; kept one release) -----------
/** Threads for an office, across every inbox. `participant` narrows to one: "mailbox:<id>" for one
 *  of our CRM inboxes, or an FTC user id — the server offers the options that have mail. */
export const getOfficeConversations = (officeId, participant) =>
  req(`/crm/offices/${officeId}/conversations/`
    + (participant ? `?participant=${encodeURIComponent(participant)}` : ""));
export const getOfficeMessages = (officeId, conversationId) =>
  req(`/crm/offices/${officeId}/messages/${conversationId ? `?conversation_id=${encodeURIComponent(conversationId)}` : ""}`);
export const composeOfficeEmail = (officeId, body) =>
  req(`/crm/offices/${officeId}/compose/`, { method: "POST", body });
export const replyOfficeEmail = (officeId, body) =>
  req(`/crm/offices/${officeId}/reply/`, { method: "POST", body });

// --- Our inboxes (CRM Settings → Email inboxes) -----------------------------
// Every Smilepass mailbox the CRM reads and sends through. `kind`: "crm" | "ftc";
// `active`: true for switched-on inboxes only. Metadata only — never credentials.
export const getMailboxes = ({ kind, active } = {}) => {
  const q = new URLSearchParams();
  if (kind) q.set("kind", kind);
  if (active) q.set("active", "1");
  const s = q.toString();
  return req(`/crm/mailboxes/${s ? "?" + s : ""}`);
};
/** {mailbox_address, display_name?, store_unmatched?, history_days?} */
export const createMailbox = (body) => req(`/crm/mailboxes/`, { method: "POST", body });
/** {display_name?, store_unmatched?, is_active?, creates_leads?} — CRM inboxes only. */
export const updateMailbox = (id, body) => req(`/crm/mailboxes/${id}/`, { method: "PATCH", body });
/** Can the app read this inbox right now? → {ok, inbox, sent} | {ok:false, status, detail} */
export const checkMailbox = (id) => req(`/crm/mailboxes/${id}/check/`, { method: "POST" });
export const uploadEmailAttachment = (file) => {
  const fd = new FormData();
  fd.append("file", file);
  return reqMultipart(`/crm/email-attachments/`, fd);
};

// --- Email templates -------------------------------------------------------
export const getTemplates = () => req(`/crm/email-templates/`);
export const createTemplate = (body) => req(`/crm/email-templates/`, { method: "POST", body });
export const updateTemplate = (id, body) => req(`/crm/email-templates/${id}/`, { method: "PATCH", body });
export const deleteTemplate = (id) => req(`/crm/email-templates/${id}/`, { method: "DELETE" });
export const getTemplateTokens = () => req(`/crm/email-templates/tokens/`);
export const previewTemplate = (id, officeId) =>
  req(`/crm/email-templates/${id}/preview/`, { method: "POST", body: { office_id: officeId } });

// --- Reminder rules --------------------------------------------------------
export const getReminderRules = () => req(`/crm/reminder-rules/`);
export const updateReminderRule = (stepKey, patch) =>
  req(`/crm/reminder-rules/${stepKey}/`, { method: "PATCH", body: patch });

// --- Manual per-readiness-step reminders (preview + send now) ---------------
export const getReadinessReminderPreview = (officeId, stepKey) =>
  req(`/crm/offices/${officeId}/readiness/${encodeURIComponent(stepKey)}/reminder-preview/`);
export const sendReadinessReminder = (officeId, stepKey, body) =>
  req(`/crm/offices/${officeId}/readiness/${encodeURIComponent(stepKey)}/send-reminder/`, {
    method: "POST",
    body,
  });

// --- FTC user management ---------------------------------------------------
// `date` (YYYY-MM-DD) scopes the HANDLED section only; omit it for today.
// Outstanding is always the live backlog — see crm.services.ftc_dashboard —
// counted against the browser's day (`today`), the same day the FTC queue
// uses, so a scheduled follow-up is "due" on both at the same moment.
export const getFtcDashboard = (date) => {
  const q = [
    `today=${encodeURIComponent(localDay())}`,
    date ? `date=${encodeURIComponent(date)}` : "",
  ].filter(Boolean).join("&");
  return req(`/crm/ftc/dashboard/?${q}`);
};
// The individual requests behind one number on the Handled table. `actor` is a
// user id, or "agent" / "unattributed".
export const getFtcDashboardRequests = ({ date, actor, office, type } = {}) => {
  const q = [
    date ? `date=${encodeURIComponent(date)}` : "",
    `actor=${encodeURIComponent(actor)}`,
    office ? `office=${encodeURIComponent(office)}` : "",
    type ? `type=${encodeURIComponent(type)}` : "",
  ].filter(Boolean).join("&");
  return req(`/crm/ftc/dashboard/requests/?${q}`);
};
export const getFtcUsers = () => req(`/crm/ftc-users/`);
export const createFtcUser = (email, password, officeIds = []) =>
  req(`/crm/ftc-users/`, { method: "POST", body: { email, password, office_ids: officeIds } });
export const inviteFtcUser = (email, officeIds = []) =>
  req(`/crm/ftc-users/invite/`, { method: "POST", body: { email, office_ids: officeIds } });
export const setFtcUserPassword = (userId, password) =>
  req(`/crm/ftc-users/${userId}/set-password/`, { method: "POST", body: { password } });
export const grantFtcOffice = (userId, officeId) =>
  req(`/crm/ftc-users/${userId}/offices/`, { method: "POST", body: { office_id: officeId } });
export const revokeFtcOffice = (userId, officeId) =>
  req(`/crm/ftc-users/${userId}/offices/${officeId}/`, { method: "DELETE" });
export const getOfficesList = () => req(`/crm/offices-list/`);

// --- FTC correspondence access (a SEPARATE grant from FTC task access above) ------
//
// Deliberately distinct endpoints and distinct wording: `grantFtcOffice` decides
// which offices' WORK an FTC sees, these decide who they may EMAIL. Widening one
// must never quietly widen the other.

/** Register (PUT) the shared O365 mailbox this FTC sends as. */
export const setFtcMailbox = (userId, mailboxAddress, displayName) =>
  req(`/crm/ftc-users/${userId}/mailbox/`, {
    method: "PUT",
    body: { mailbox_address: mailboxAddress, display_name: displayName || "" },
  });

/** Deactivate it. The logged correspondence survives — it's the office's history. */
export const clearFtcMailbox = (userId) =>
  req(`/crm/ftc-users/${userId}/mailbox/`, { method: "DELETE" });

export const getFtcAssignments = (userId) => req(`/crm/ftc-users/${userId}/assignments/`);

/** Create or REPLACE one office's grant. contactIds is the complete permitted set. */
export const saveFtcAssignment = (userId, officeId, contactIds) =>
  req(`/crm/ftc-users/${userId}/assignments/`, {
    method: "POST",
    body: { office_id: officeId, contact_ids: contactIds || [] },
  });

export const revokeFtcAssignment = (assignmentId) =>
  req(`/crm/ftc-assignments/${assignmentId}/`, { method: "DELETE" });

// --- clinic value reports (super-admin rollout view) ------------------------
//
// The FTC's own /ftc-reports page configures one clinic at a time. This is the
// cross-office picture: which clinics are switched on, on whose signature, and
// which are enabled but would silently send nothing.

export const getReportSchedules = () => req(`/crm/report-schedules/`);

export const updateReportSchedule = (body) =>
  req(`/crm/report-schedules/`, { method: "PUT", body });

/** What a clinic would receive — recipients resolved from the SIGNER, not the caller. */
export const getCrmReportPreview = (officeId, period) =>
  req(
    `/crm/report-schedules/preview/?office_id=${encodeURIComponent(officeId)}` +
      `&period=${encodeURIComponent(period || "monthly")}`,
  );

/** Send one now. Goes out over the assigned FTC's name, not the admin's. */
export const sendCrmReport = (body) =>
  req(`/crm/report-schedules/send/`, { method: "POST", body });

// --- one office's reporting + correspondence (office drawer, Analytics) -------

export const getOfficeReportSchedule = (officeId) =>
  req(`/crm/offices/${officeId}/report-schedule/`);

export const saveOfficeReportSchedule = (officeId, body) =>
  req(`/crm/offices/${officeId}/report-schedule/`, { method: "PUT", body });

/** Which FTCs may correspond with this office, and the contacts each may email. */
export const getOfficeFtcAccess = (officeId) => req(`/crm/offices/${officeId}/ftc-access/`);

/** Create or REPLACE one FTC's grant here. contactIds is the complete permitted set. */
export const saveOfficeFtcAccess = (officeId, ftcUserId, contactIds) =>
  req(`/crm/offices/${officeId}/ftc-access/`, {
    method: "POST",
    body: { ftc_user_id: ftcUserId, contact_ids: contactIds || [] },
  });

// --- Office user management (per-office; reuse the clinics endpoints — a superuser
// passes MasterAccessOrSuperuser) + super-admin-only account/matrix actions ---------
export const getOfficeUsers = (officeId) => req(`/clinics/office/${officeId}/users/`);
export const createOfficeUser = (officeId, email, password) =>
  req(`/clinics/office/${officeId}/users/`, { method: "POST", body: { email, password } });
export const inviteOfficeUser = (officeId, email) =>
  req(`/clinics/office/${officeId}/users/invite/`, { method: "POST", body: { email } });
export const setOfficeUserPassword = (officeId, userId, password) =>
  req(`/clinics/office/${officeId}/users/${userId}/set-password/`, { method: "POST", body: { password } });
export const removeOfficeUser = (officeId, userId) =>
  req(`/clinics/office/${officeId}/users/${userId}/remove/`, { method: "POST" });
export const setUserActive = (userId, isActive) =>
  req(`/crm/users/${userId}/set-active/`, { method: "POST", body: { is_active: isActive } });
export const getPracticeUsers = (practiceId) => req(`/crm/practices/${practiceId}/users/`);
export const setUserOfficeAccess = (practiceId, userId, officeId, grant) =>
  req(`/crm/practices/${practiceId}/users/${userId}/office-access/`, {
    method: "POST", body: { office_id: officeId, grant },
  });

// --- Carrier Knowledge (super admin) ----------------------------------------
// The playbooks FTC read in the insurance modal, managed as a whole.

export const getCarrierKnowledgeOverview = () =>
  req("/clinics/carrier-knowledge/overview/");

// Other names PMSs use for our carriers ("ROYAL BANK INSURANCE" → RBC), and PMS
// carriers nobody could name yet. See bookc-backend clinics/views_carrier_aliases.py.
export const getCarrierAliases = (carrierId) =>
  req(`/clinics/carrier-aliases/${carrierId ? `?provider=${carrierId}` : ""}`);
export const addCarrierAlias = (name, carrierId) =>
  req("/clinics/carrier-aliases/", { method: "POST", body: { name, provider_id: carrierId } });
export const removeCarrierAlias = (aliasId) =>
  req(`/clinics/carrier-aliases/${aliasId}/`, { method: "DELETE" });
export const getUnresolvedPmsCarriers = () => req("/clinics/unresolved-pms-carriers/");
export const markCarrierReviewed = (carrierId) =>
  req(`/clinics/insurance-providers/${carrierId}/`, {
    method: "PATCH", body: { reviewed_at: new Date().toISOString() },
  });

export const getCarrierPlaybook = (carrierId, taskType) =>
  req(`/clinics/carrier-playbook/?carrier_id=${carrierId}&task_type=${taskType}`);

export const getCarrierPlaybookHistory = (carrierId, taskType) =>
  req(`/clinics/carrier-playbook/history/?carrier_id=${carrierId}&task_type=${taskType}`);

export const saveCarrierPlaybook = (carrierId, taskType, fields) =>
  req("/clinics/carrier-playbook/edit/", {
    method: "PATCH",
    body: Object.assign({ carrier_id: carrierId, task_type: taskType }, fields),
  });

export const revertCarrierPlaybook = (carrierId, taskType, version) =>
  req("/clinics/carrier-playbook/revert/", {
    method: "POST",
    body: { carrier_id: carrierId, task_type: taskType, version },
  });

export const getCarrierKnowledgeReports = (params = {}) => {
  const q = new URLSearchParams();
  if (params.status) q.set("status", params.status);
  if (params.carrierId) q.set("carrier_id", params.carrierId);
  const qs = q.toString();
  return req(`/clinics/carrier-knowledge/reports/${qs ? "?" + qs : ""}`);
};

export const retryCarrierReport = (reportId) =>
  req(`/clinics/carrier-knowledge/reports/${reportId}/retry/`, { method: "POST" });

// Which offices hold a portal login for this carrier — and which don't. Metadata
// only; the secret never leaves the office's own audited panel.
export const getCarrierCredentialCoverage = (carrierId) =>
  req(`/clinics/carrier-knowledge/credentials/?carrier_id=${carrierId}`);

// The vocabulary that turns what a rep SAID into a procedure we can store.
// Omitting carrierId reads/writes the shared table; passing it targets that
// carrier's own overrides.
export const getCarrierLexicon = (carrierId) =>
  req(`/clinics/carrier-knowledge/lexicon/${carrierId ? "?carrier_id=" + carrierId : ""}`);

export const saveLexiconTerm = (body) =>
  req("/clinics/carrier-knowledge/lexicon/", { method: "POST", body });

export const deleteLexiconTerm = (body) =>
  req("/clinics/carrier-knowledge/lexicon/", { method: "DELETE", body });


// ---------------------------------------------------------------------------
// Leads — prospects before (and after) they become practices
// ---------------------------------------------------------------------------

// `tags`: array of tag ids (repeatable ?tag=); `status`: new|invited|registered|converted.
export const getLeads = ({ page = 1, pageSize = 25, search, tags, status, source } = {}) => {
  let q = `?page=${page}&page_size=${pageSize}`;
  if (search) q += `&search=${encodeURIComponent(search)}`;
  (tags || []).forEach((t) => { if (t) q += `&tag=${encodeURIComponent(t)}`; });
  if (status) q += `&status=${encodeURIComponent(status)}`;
  if (source) q += `&source=${encodeURIComponent(source)}`;
  return req(`/crm/leads/${q}`);
};
// Body: the lead's fields (see LEAD_WRITABLE_FIELDS on the server) plus optional
// principal_dentist {name,email,phone}, contacts [{role,name,email,phone,title}],
// tag_ids, new_tags, and send_booking_email (true = email them now).
export const createLead = (body) => req(`/crm/leads/`, { method: "POST", body });
export const getLead = (id) => req(`/crm/leads/${id}/`);
export const updateLead = (id, body) => req(`/crm/leads/${id}/`, { method: "PATCH", body });
export const deleteLead = (id) => req(`/crm/leads/${id}/`, { method: "DELETE" });
export const getLeadContacts = (id) => req(`/crm/leads/${id}/contacts/`);
export const addLeadContact = (id, body) => req(`/crm/leads/${id}/contacts/`, { method: "POST", body });
// Replaces the lead's tag set. `newTags` are created (case-insensitively unique).
export const setLeadTags = (id, { tagIds = [], newTags = [] } = {}) =>
  req(`/crm/leads/${id}/tags/`, { method: "PUT", body: { tag_ids: tagIds, new_tags: newTags } });

// Super-admin-defined tags (global). GET returns a plain array with lead_count.
export const getLeadTags = () => req(`/crm/lead-tags/`);
export const createLeadTag = (body) => req(`/crm/lead-tags/`, { method: "POST", body });
export const updateLeadTag = (id, body) => req(`/crm/lead-tags/${id}/`, { method: "PATCH", body });
export const deleteLeadTag = (id) => req(`/crm/lead-tags/${id}/`, { method: "DELETE" });

// The "book a call" email: preview (rendered subject/body + recipients), then send as edited.
export const getLeadBookingEmailPreview = (id) => req(`/crm/leads/${id}/booking-email/preview/`);
export const sendLeadBookingEmail = (id, { subject, bodyHtml, recipients } = {}) =>
  req(`/crm/leads/${id}/booking-email/send/`, {
    method: "POST", body: { subject, body_html: bodyHtml, recipients },
  });
// CRM Settings: {auto_booking_email_enabled, booking_template_id, booking_template_name, email_enabled}
// Which optional registration onboarding steps new registrants see (CRM Settings).
export const getOnboardingSettings = () => req(`/crm/onboarding-settings/`);
export const updateOnboardingSettings = (body) =>
  req(`/crm/onboarding-settings/`, { method: "PATCH", body });
export const getLeadSettings = () => req(`/crm/lead-settings/`);
export const updateLeadSettings = (body) => req(`/crm/lead-settings/`, { method: "PATCH", body });
// {url, configured, secret_length, fields:[{name,label,required}], last_received_at, submissions_30d, leads_by_source}
export const getFramerWebhookInfo = () => req(`/crm/framer-webhook/`);

// The practice side: its lead (created on the spot if missing) and its tags.
export const getPracticeLead = (practiceId) => req(`/crm/practices/${practiceId}/lead/`);
export const setPracticeLeadTags = (practiceId, { tagIds = [], newTags = [] } = {}) =>
  req(`/crm/practices/${practiceId}/lead/tags/`, { method: "PUT", body: { tag_ids: tagIds, new_tags: newTags } });

// Dormant. Body: {office_ids?, reason, tag_ids, new_tags, note:{body,is_task,due_date,assignee_id}}.
// One request applies flag + tags + note atomically. DELETE reactivates.
export const markPracticeDormant = (practiceId, body) =>
  req(`/crm/practices/${practiceId}/dormant/`, { method: "POST", body });
export const reactivatePractice = (practiceId, body) =>
  req(`/crm/practices/${practiceId}/dormant/`, { method: "DELETE", body });
export const markOfficeDormant = (officeId, body) =>
  req(`/crm/offices/${officeId}/dormant/`, { method: "POST", body });
export const reactivateOffice = (officeId) =>
  req(`/crm/offices/${officeId}/dormant/`, { method: "DELETE" });
// Demo mode: two demo patients + the demo source office's SunLife/ProviderConnect logins.
export const enableOfficeDemoMode = (officeId) =>
  req(`/crm/offices/${officeId}/demo-mode/`, { method: "POST" });
export const disableOfficeDemoMode = (officeId) =>
  req(`/crm/offices/${officeId}/demo-mode/`, { method: "DELETE" });
